import { useState, useEffect, useCallback, useRef } from 'react';
import useApi from '../useApi';
import axiosInstance from '../axiosInstance';

const BASE = '/geo/regions';

// ─── Plain API (exported for use outside hooks) ─────────────────────────────

export const getRegions = (params = {}) =>
  axiosInstance.get(BASE, { params }).then((res) => res.data);

export const getRegionById = (id) =>
  axiosInstance.get(`${BASE}/${id}`).then((res) => res.data);

export const createRegion = (data) =>
  axiosInstance.post(BASE, data).then((res) => res.data);

export const updateRegion = (id, data) =>
  axiosInstance.put(`${BASE}/${id}`, data).then((res) => res.data);

export const deleteRegion = (id) =>
  axiosInstance.delete(`${BASE}/${id}`).then((res) => res.data);

export const toggleRegionActive = (id) =>
  axiosInstance.patch(`${BASE}/${id}/toggle-active`).then((res) => res.data);

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * useGetRegions - fetch regions list with params
 * @param {Object} params - { page, limit, search, countryId, active, sortBy, sortOrder }
 * @returns { data, pagination, loading, error, refetch }
 */
export const useGetRegions = (params = {}) => {
  const {
    page = 1,
    limit = 10,
    search = '',
    countryId = '',
    active = '',
    sortBy = 'name',
    sortOrder = 'ASC',
  } = params;

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Guards against out-of-order responses: when filters change, an older
  // (e.g. unfiltered) request can resolve AFTER the newer one and clobber state.
  const reqIdRef = useRef(0);

  const fetchRegions = useCallback(async () => {
    const myId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const queryParams = {
        page,
        limit,
        sortBy,
        sortOrder,
      };
      if (search) queryParams.search = search;
      if (countryId) queryParams.countryId = countryId;
      if (active !== '') queryParams.active = active;

      const response = await axiosInstance.get(BASE, { params: queryParams });
      if (myId !== reqIdRef.current) return; // a newer request superseded this one
      const res = response.data;

      if (res.success) {
        setData(res.data || []);
        if (res.pagination) {
          setPagination(res.pagination);
        }
      } else {
        setError(res.message || 'Failed to load regions');
        setData([]);
      }
    } catch (err) {
      if (myId !== reqIdRef.current) return;
      if (err.response) {
        setError(err.response?.data?.message || err.response?.data?.error || `Request failed (${err.response.status})`);
      } else if (err.request) {
        setError('Cannot reach server. Check that the backend is running (e.g. http://localhost:5000).');
      } else {
        setError(err.message || 'Failed to load regions');
      }
      setData([]);
    } finally {
      if (myId === reqIdRef.current) setLoading(false);
    }
  }, [page, limit, search, countryId, active, sortBy, sortOrder]);

  useEffect(() => {
    fetchRegions();
  }, [fetchRegions]);

  const refetch = useCallback(() => {
    fetchRegions();
  }, [fetchRegions]);

  return { data, pagination, loading, error, refetch };
};

/**
 * useCreateRegion - create region (useApi pattern)
 */
export const useCreateRegion = () => {
  return useApi(BASE, { method: 'POST' });
};

/**
 * useUpdateRegion - update region by id
 * @returns { execute(id, payload), loading, error }
 */
export const useUpdateRegion = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id, payload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.put(`${BASE}/${id}`, payload);
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update region';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

/**
 * useDeleteRegion - delete region by id
 */
export const useDeleteRegion = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.delete(`${BASE}/${id}`);
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to delete region';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

/**
 * useToggleRegionActive - toggle active status
 */
export const useToggleRegionActive = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.patch(`${BASE}/${id}/toggle-active`);
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to toggle status';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};
